import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { listEvalBanks, type BankSummary } from '@/lib/api';
import {
  runAnswerability,
  useAnswerability,
  type QuestionVerdict,
} from '@/lib/useAnswerability';
import { cn } from '@/lib/utils';
import { describeApiError } from '@/lib/apiErrors';
import { Message } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';
import { fieldClass } from '@/components/ui/field';

const VERDICT_LABEL: Record<QuestionVerdict['verdict'], string> = {
  answerable: 'Answerable',
  not_answerable: 'Not answerable',
  unverified: 'Not proven',
  failed: 'No verdict',
};

/**
 * "Can your ontology answer these questions?" — the answerability check
 * (list 22.10): each question of a Test Bed bank is read against the schema
 * alone, before any data is loaded, and comes back answerable with the query
 * that would answer it, or not answerable with what the ontology lacks.
 */
export function AnswerabilitySection({ token }: { token: string | undefined }) {
  const queryClient = useQueryClient();
  const status = useAnswerability(token);
  const [banks, setBanks] = useState<BankSummary[] | null>(null);
  const [bankError, setBankError] = useState<string | null>(null);
  const [bankId, setBankId] = useState('');
  const [starting, setStarting] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let live = true;
    void listEvalBanks(token).then((r) => {
      if (!live) return;
      if (r.ok) {
        setBanks(r.data.banks);
        const first = r.data.banks.find((b) => b.question_count > 0);
        setBankId((cur) => cur || first?.id || '');
      } else {
        setBankError(r.message);
      }
    });
    return () => {
      live = false;
    };
  }, [token]);

  const running = status.data?.running ?? null;
  const last = status.data?.last ?? null;
  const lastFailure = status.data?.last_failure ?? null;

  async function start() {
    if (!token || !bankId) return;
    setStarting(true);
    setRunError(null);
    const r = await runAnswerability(bankId, token);
    setStarting(false);
    if (r.ok) queryClient.setQueryData(['answerability'], r.data);
    else setRunError(r.message);
  }

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-tres-text-secondary">
        Can it answer your questions?
      </h3>
      <p className="text-xs text-tres-text-secondary">
        Checks each question in a Test Bed question bank against the ontology alone, before any
        data is loaded: answerable, with the query that would answer it, or not, with what the
        ontology lacks. It asks the AI model once per question.
      </p>

      {bankError && (
        <Message kind="warning" density="compact">
          The question banks could not be listed: {bankError}
        </Message>
      )}
      {banks && banks.length === 0 && (
        <p className="text-xs text-tres-text-secondary">
          No question banks yet. Create one in the LLM Test Bed with the questions you need
          answered, then run the check here.
        </p>
      )}
      {banks && banks.length > 0 && (
        <div className="flex items-end gap-2">
          <button
            type="button"
            disabled={!bankId || starting || running !== null}
            onClick={() => void start()}
            className={buttonVariants({ variant: 'primary', size: 'sm' })}
          >
            {starting ? 'Starting…' : 'Run the check'}
          </button>
          <label className="min-w-0 flex-1 text-xs text-tres-text-secondary">
            <span className="mb-1 block">Question bank</span>
            <select
              value={bankId}
              onChange={(e) => setBankId(e.target.value)}
              className={fieldClass('normal', 'w-full text-sm')}
            >
              {banks.map((b) => (
                <option key={b.id} value={b.id} disabled={b.question_count === 0}>
                  {b.name} ({b.question_count} question{b.question_count === 1 ? '' : 's'})
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {runError && (
        <Message kind="warning" density="compact">
          The check did not start: {runError}
        </Message>
      )}
      {running && (
        <p className="text-sm text-tres-text-secondary" role="status">
          {running.waiting_for_index
            ? `Waiting for the index to take in recent changes, so ${running.bank_name} is checked against the ontology as it now stands.`
            : `Checking ${running.bank_name}: ${running.done} of ${running.total} questions done.`}
        </p>
      )}
      {lastFailure && !running && (
        <Message kind="warning" density="compact">
          The check of {lastFailure.bank_name} started {new Date(lastFailure.started_at).toLocaleString()} did
          not finish. {describeApiError(500, lastFailure.error)}
        </Message>
      )}
      {last && (
        <div className="space-y-2">
          <p className="text-xs text-tres-text-secondary">
            {last.report.bank_name}, checked {new Date(last.finished_at).toLocaleString()} with{' '}
            {last.report.model}: {last.report.answerable} of {last.report.questions.length} answerable
            {last.report.not_answerable ? `, ${last.report.not_answerable} not answerable` : ''}
            {last.report.unverified ? `, ${last.report.unverified} not proven` : ''}
            {last.report.failed ? `, ${last.report.failed} without a verdict` : ''}.
          </p>
          <ul className="space-y-2">
            {last.report.questions.map((q, i) => (
              <li key={i} className="rounded border border-border p-2 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-tres-text-primary">{q.question}</span>
                  <span
                    className={cn(
                      'shrink-0 text-xs font-medium',
                      q.verdict === 'answerable' ? 'text-tres-success' : 'text-tres-error',
                    )}
                  >
                    {VERDICT_LABEL[q.verdict]}
                  </span>
                </div>
                {q.query && (
                  <pre className="mt-1 overflow-x-auto whitespace-pre-wrap rounded bg-muted px-2 py-1 font-mono text-xs">
                    {q.query}
                  </pre>
                )}
                {q.missing.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-xs">
                    {q.missing.map((m, j) => (
                      <li key={j}>
                        Missing {m.what.replace('_', '/')}
                        {m.term ? <span className="font-mono"> {m.term}</span> : null}: {m.detail}
                      </li>
                    ))}
                  </ul>
                )}
                {q.note && <p className="mt-1 text-xs text-tres-error">{q.note}</p>}
                {q.explanation && (
                  <p className="mt-1 text-xs text-tres-text-secondary">{q.explanation}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
